import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQuiz } from '../../contexts/QuizContext';
import { CreateQuizData, Quiz } from '../../services/quizService';
import { Question } from '../../services/questionService';
import QuestionSelector from './QuestionSelector';

interface QuizCreatorProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (quizId: number) => void;
  preSelectedDocument?: any; // Document type from documentService
  editingQuiz?: Quiz; // Quiz to edit
}

interface QuizFormData {
  title: string;
  description: string;
  time_limit: number | null;
  shuffle_questions: boolean;
  shuffle_answers: boolean;
  is_public: boolean;
  require_login: boolean;
  max_attempts: number | null;
  show_results: boolean;
  show_correct_answers: boolean;
  selected_questions: Question[];
  points_per_question: number[];
}

const steps = [
  'Thông tin cơ bản',
  'Chọn câu hỏi',
  'Cài đặt nâng cao',
];

const QuizCreator: React.FC<QuizCreatorProps> = ({ open, onClose, onSuccess, preSelectedDocument, editingQuiz }) => {
  const { createQuiz, updateQuiz, quizLoading, quizError, clearAllErrors } = useQuiz();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<QuizFormData>({
    title: '',
    description: '',
    time_limit: null,
    shuffle_questions: false,
    shuffle_answers: false,
    is_public: true,
    require_login: false,
    max_attempts: null,
    show_results: true,
    show_correct_answers: true,
    selected_questions: [],
    points_per_question: [],
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setErrors({});
      clearAllErrors();

      if (editingQuiz) {
        // Load existing quiz data for editing
        setFormData({
          title: editingQuiz.title || '',
          description: editingQuiz.description || '',
          time_limit: editingQuiz.time_limit,
          shuffle_questions: editingQuiz.shuffle_questions,
          shuffle_answers: editingQuiz.shuffle_answers,
          is_public: editingQuiz.is_public,
          require_login: editingQuiz.require_login,
          max_attempts: editingQuiz.max_attempts,
          show_results: editingQuiz.show_results,
          show_correct_answers: editingQuiz.show_correct_answers,
          // Limit to actual quiz questions count to avoid showing unrelated questions
          selected_questions: (editingQuiz.quiz_questions?.map(q => q.question) || []).slice(0, editingQuiz.questions_count || undefined),
          points_per_question: editingQuiz.quiz_questions?.map(q => parseFloat(q.points.toString())) || [],
        });
      } else {
        // Reset form for new quiz
        setFormData({
          title: '',
          description: '',
          time_limit: null,
          shuffle_questions: false,
          shuffle_answers: false,
          is_public: true,
          require_login: false,
          max_attempts: null,
          show_results: true,
          show_correct_answers: true,
          selected_questions: [],
          points_per_question: [],
        });
      }
    }
  }, [open, editingQuiz, clearAllErrors]);

  const validateStep = (step: number): boolean => {
    const newErrors: { [key: string]: string } = {};

    switch (step) {
      case 0: // Basic Information
        if (!formData.title.trim()) {
          newErrors.title = 'Tiêu đề bài tập là bắt buộc';
        } else if (formData.title.trim().length < 3) {
          newErrors.title = 'Tiêu đề phải có ít nhất 3 ký tự';
        }

        if (formData.time_limit !== null && formData.time_limit <= 0) {
          newErrors.time_limit = 'Thời gian làm bài phải lớn hơn 0';
        }

        if (formData.max_attempts !== null && formData.max_attempts <= 0) {
          newErrors.max_attempts = 'Số lần làm bài tối đa phải lớn hơn 0';
        }
        break;

      case 1: // Question Selection
        if (formData.selected_questions.length === 0) {
          newErrors.questions = 'Bài tập phải có ít nhất 1 câu hỏi';
        }
        break;

      case 2: // Advanced Settings
        // No specific validation for advanced settings
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleInputChange = (field: keyof QuizFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleQuestionsSelected = (questions: Question[]) => {
    const points = questions.map(() => 1); // Default 1 point per question
    setFormData(prev => ({
      ...prev,
      selected_questions: questions,
      points_per_question: points,
    }));

    // Clear questions error if questions are selected
    if (questions.length > 0 && errors.questions) {
      setErrors(prev => ({ ...prev, questions: '' }));
    }
  };

  const handlePointsChange = (index: number, points: number) => {
    const newPoints = [...formData.points_per_question];
    newPoints[index] = points;
    setFormData(prev => ({ ...prev, points_per_question: newPoints }));
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    // Convert points_per_question array to object with question IDs as keys
    const pointsPerQuestion: { [key: string]: number } = {};
    formData.selected_questions.forEach((question, index) => {
      pointsPerQuestion[question.id.toString()] = formData.points_per_question[index] || 1;
    });

    if (editingQuiz) {
      // Update existing quiz
      const updateData = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        time_limit: formData.time_limit,
        shuffle_questions: formData.shuffle_questions,
        shuffle_answers: formData.shuffle_answers,
        is_public: formData.is_public,
        require_login: formData.require_login,
        max_attempts: formData.max_attempts,
        show_results: formData.show_results,
        show_correct_answers: formData.show_correct_answers,
        // ADD: Include question_ids and points_per_question for quiz updates
        question_ids: formData.selected_questions.map(q => q.id),
        points_per_question: pointsPerQuestion,
      };

      const result = await updateQuiz(editingQuiz.id, updateData);
      if (result) {
        onSuccess?.(result.id);
        onClose();
      }
    } else {
      // Create new quiz
      const quizData: CreateQuizData = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        question_ids: formData.selected_questions.map(q => q.id),
        time_limit: formData.time_limit,
        shuffle_questions: formData.shuffle_questions,
        shuffle_answers: formData.shuffle_answers,
        is_public: formData.is_public,
        require_login: formData.require_login,
        max_attempts: formData.max_attempts,
        show_results: formData.show_results,
        show_correct_answers: formData.show_correct_answers,
        points_per_question: pointsPerQuestion,
      };

      const result = await createQuiz(quizData);
      if (result) {
        onSuccess?.(result.id);
        onClose();
      }
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Tiêu đề bài tập"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              sx={{ mb: 3 }}
              required
            />

            <TextField
              fullWidth
              label="Mô tả bài tập"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              multiline
              rows={3}
              sx={{ mb: 3 }}
              helperText="Mô tả ngắn gọn về nội dung bài tập"
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label="Thời gian làm bài (phút)"
                type="number"
                value={formData.time_limit || ''}
                onChange={(e) => handleInputChange('time_limit', e.target.value ? parseInt(e.target.value) : null)}
                error={!!errors.time_limit}
                helperText={errors.time_limit || 'Để trống nếu không giới hạn thời gian'}
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
              <TextField
                fullWidth
                label="Số lần làm tối đa"
                type="number"
                value={formData.max_attempts || ''}
                onChange={(e) => handleInputChange('max_attempts', e.target.value ? parseInt(e.target.value) : null)}
                error={!!errors.max_attempts}
                helperText={errors.max_attempts || 'Để trống nếu không giới hạn'}
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            {errors.questions && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.questions}
              </Alert>
            )}

            <QuestionSelector
              selectedQuestions={formData.selected_questions}
              onQuestionsChange={handleQuestionsSelected}
              preSelectedDocument={preSelectedDocument}
              quizId={editingQuiz?.id}
              initialTotal={editingQuiz?.questions_count}
            />

            {formData.selected_questions.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Điểm số cho từng câu hỏi
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {formData.selected_questions.map((question, index) => (
                    <Box key={question.id} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: 'break-word',
                              lineHeight: 1.5,
                              mb: { xs: 1, sm: 0 }
                            }}
                          >
                            {question.text}
                          </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                          <TextField
                            size="small"
                            label="Điểm"
                            type="number"
                            value={formData.points_per_question[index] || 1}
                            onChange={(e) => handlePointsChange(index, parseInt(e.target.value) || 1)}
                            InputProps={{
                              inputProps: { min: 1, max: 10 }
                            }}
                            sx={{
                              width: { xs: '100%', sm: '100px' },
                              minWidth: '100px'
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Cài đặt quyền truy cập
            </Typography>
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_public}
                    onChange={(e) => {
                      const isPublic = e.target.checked;
                      handleInputChange('is_public', isPublic);
                      // Logic mới: Public = không cần login, Private = cần login
                      handleInputChange('require_login', !isPublic);
                    }}
                  />
                }
                label={formData.is_public ? "Công khai (ai cũng có thể làm)" : "Riêng tư (chỉ bạn có thể làm)"}
              />

              <Alert severity="info" sx={{ mt: 2 }}>
                {formData.is_public
                  ? "Quiz công khai: Mọi người có thể truy cập và làm bài mà không cần đăng nhập."
                  : "Quiz riêng tư: Chỉ bạn có thể truy cập và làm bài (yêu cầu đăng nhập)."
                }
              </Alert>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Cài đặt câu hỏi
            </Typography>
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.shuffle_questions}
                    onChange={(e) => handleInputChange('shuffle_questions', e.target.checked)}
                  />
                }
                label="Xáo trộn thứ tự câu hỏi"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.shuffle_answers}
                    onChange={(e) => handleInputChange('shuffle_answers', e.target.checked)}
                  />
                }
                label="Xáo trộn thứ tự đáp án"
                sx={{ display: 'block' }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Cài đặt kết quả
            </Typography>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_results}
                    onChange={(e) => handleInputChange('show_results', e.target.checked)}
                  />
                }
                label="Hiển thị kết quả ngay sau khi làm xong"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.show_correct_answers}
                    onChange={(e) => handleInputChange('show_correct_answers', e.target.checked)}
                    disabled={!formData.show_results}
                  />
                }
                label="Hiển thị đáp án đúng"
                sx={{ display: 'block' }}
              />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {editingQuiz ? 'Chỉnh sửa bài tập trắc nghiệm' : 'Tạo bài tập trắc nghiệm mới'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {quizError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {quizError}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Quay lại
        </Button>

        <Box sx={{ flex: '1 1 auto' }} />

        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={quizLoading}
          >
            {quizLoading
              ? (editingQuiz ? 'Đang cập nhật...' : 'Đang tạo...')
              : (editingQuiz ? 'Cập nhật bài tập' : 'Tạo bài tập')
            }
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
          >
            Tiếp theo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuizCreator;
